import { useState } from "react";
import type { ApiError } from "@/lib/api";
import { errorMessage } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/Button";
import { TkticSelect } from "@/components/ui/TkticSelect";
import type { Player } from "@/lib/types";
import { CoachModal } from "./CoachModal";
import type { RosterMutations } from "../hooks/useRoster";
import { PLAYER_POSITION_OPTIONS } from "../playerPositions";

/**
 * Player create / edit modal — Coach > Roster.
 *
 * Ports the legacy `openCoachPlayerEditModal` + the Quick Add
 * panel (`js/coaching/roster.js`). One component covers both: a
 * `null` `player` prop ⇒ create (Quick Add), a `Player` ⇒ edit.
 *
 * Fields mirror `CreatePlayerRequest` / `UpdatePlayerRequest`:
 *   - display_name (required)
 *   - jersey_number
 *   - position
 *   - notes
 *   - active (edit only — Quick Add always creates active)
 *
 * `UpdatePlayerRequest` is `extra="forbid"`; on edit we send only
 * the changed fields. The backend strips whitespace server-side
 * (the request validators) — we don't double-trim here beyond the
 * required-name check.
 */

export type PlayerModalProps = {
  /** `null` ⇒ create (Quick Add); a `Player` ⇒ edit that player. */
  player: Player | null;
  mutations: RosterMutations;
  onClose: () => void;
  /** Called after a successful create/update so the page can show
   * a toast / refocus. The roster list refetches via
   * `invalidateScope` inside the mutation regardless. */
  onSaved?: () => void;
};

export function PlayerModal({
  player,
  mutations,
  onClose,
  onSaved,
}: PlayerModalProps) {
  const isEdit = player !== null;
  const toast = useToast();
  const [displayName, setDisplayName] = useState(player?.display_name ?? "");
  const [jersey, setJersey] = useState(player?.jersey_number ?? "");
  const [position, setPosition] = useState(player?.position ?? "");
  const [notes, setNotes] = useState(player?.notes ?? "");
  const [active, setActive] = useState(player?.active ?? true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = displayName.trim();
    if (!name) {
      setError("Player name is required.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      if (isEdit) {
        // `extra="forbid"` — send only the changed fields.
        const patch: Record<string, unknown> = {};
        if (name !== player.display_name) patch.display_name = name;
        if (jersey !== player.jersey_number) patch.jersey_number = jersey;
        if (position !== (player.position ?? "")) patch.position = position;
        if (notes !== player.notes) patch.notes = notes;
        if (active !== player.active) patch.active = active;
        // Nothing changed — close without a no-op PATCH.
        if (Object.keys(patch).length > 0) {
          await mutations.updatePlayer(player.id, patch);
        }
      } else {
        await mutations.createPlayer({
          avatar_style: "neutral",
          display_name: name,
          jersey_number: jersey,
          position,
          notes,
          active,
        });
      }
      toast.success(isEdit ? "Player saved" : "Player added");
      onSaved?.();
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        typeof apiErr?.detail === "string"
          ? apiErr.detail
          : `Could not ${isEdit ? "update" : "add"} the player.`,
      );
      toast.error(`Couldn't ${isEdit ? "save" : "add"} player`, {
        description: errorMessage(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CoachModal
      title={isEdit ? "Edit player" : "Quick Add player"}
      onClose={onClose}
      data-testid="roster-player-modal"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="player-editor-form"
            disabled={submitting}
            data-testid="player-modal-submit"
          >
            {submitting ? "Saving…" : isEdit ? "Save player" : "Add player"}
          </Button>
        </div>
      }
    >
      <form
        id="player-editor-form"
        className="flex flex-col gap-3"
        onSubmit={handleSubmit}
      >
        <Field label="Player name *">
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            data-testid="player-modal-name"
            autoFocus
            className="w-full bg-canvas border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Jersey number">
            <input
              type="text"
              value={jersey}
              onChange={(e) => setJersey(e.target.value)}
              data-testid="player-modal-jersey"
              className="w-full bg-canvas border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </Field>
          <Field label="Position">
            <TkticSelect
              value={position}
              onChange={setPosition}
              data-testid="player-modal-position"
              size="sm"
              ariaLabel="Position"
              options={PLAYER_POSITION_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
          </Field>
          <Field label="Status">
            <TkticSelect
              value={active ? "active" : "inactive"}
              onChange={(next) => setActive(next === "active")}
              data-testid="player-modal-active"
              size="sm"
              ariaLabel="Status"
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
            />
          </Field>
        </div>
        <Field label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="player-modal-notes"
            rows={2}
            placeholder="Optional roster note"
            className="min-h-[4.375rem] w-full resize-y bg-canvas border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>

        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="player-modal-error"
          >
            {error}
          </p>
        ) : null}
      </form>
    </CoachModal>
  );
}

function Field(props: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
      </span>
      {props.children}
      {props.helper ? (
        <span className="text-xs text-ink-muted">{props.helper}</span>
      ) : null}
    </label>
  );
}
